import type { ReactElement } from 'react';

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import PageLayout from '@/components/PageLayout';
import { Users, UserPlus, UserCheck, Loader2, ArrowLeft, User, Sparkles, BookOpen, Heart } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 20;

type TabType = 'following' | 'followers';

interface ConnectionUser {
  user_id: string;
  name: string;
  handle?: string;
  picture?: string;
  bio?: string;
  story_count?: number;
  is_following: boolean;
}

interface ConnectionsResponse {
  users: ConnectionUser[];
  has_more: boolean;
  total: number;
}

interface FollowResponse {
  is_following: boolean;
}

// Helper to get full image URL
const getImageUrl = (url: string | undefined | null): string | null => {
  if (!url) return null;
  if (url.startsWith('/static/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

const ConnectionsPage = (): ReactElement => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as TabType) || 'following';

  const [activeTab, setActiveTab] = useState<TabType>(initialTab);
  const [users, setUsers] = useState<ConnectionUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [offset, setOffset] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [total, setTotal] = useState<number>(0);
  const [followLoadingIds, setFollowLoadingIds] = useState<Set<string>>(new Set());

  const loaderRef = useRef<HTMLDivElement>(null);

  // Load data when tab changes
  useEffect(() => {
    setUsers([]);
    setOffset(0);
    setHasMore(true);
    loadData(0, true);
  }, [activeTab]);

  // Infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    const currentLoader = loaderRef.current;
    if (currentLoader) observer.observe(currentLoader);

    return () => {
      if (currentLoader) observer.unobserve(currentLoader);
    };
  }, [hasMore, loading, loadingMore]);

  const loadData = async (currentOffset: number = 0, isReset: boolean = false): Promise<void> => {
    if (isReset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const endpoint = activeTab === 'following' ? 'following' : 'followers';
      const response = await axios.get<ConnectionsResponse>(
        `${API}/profile/${endpoint}?limit=${PAGE_SIZE}&offset=${currentOffset}`,
        { withCredentials: true }
      );

      const newUsers = response.data.users || [];
      setHasMore(response.data.has_more);
      setTotal(response.data.total);

      if (isReset) {
        setUsers(newUsers);
      } else {
        setUsers(prev => [...prev, ...newUsers]);
      }
    } catch (error) {
      console.error('Failed to load connections:', error);
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = (): void => {
    const newOffset = offset + PAGE_SIZE;
    setOffset(newOffset);
    loadData(newOffset, false);
  };

  const handleFollow = async (userId: string): Promise<void> => {
    setFollowLoadingIds(prev => new Set(prev).add(userId));

    try {
      const response = await axios.post<FollowResponse>(
        `${API}/profile/${userId}/follow`,
        {},
        { withCredentials: true }
      );

      // Update the user's is_following status in the list
      setUsers(prev => prev.map(user =>
        user.user_id === userId
          ? { ...user, is_following: response.data.is_following }
          : user
      ));

      toast.success(response.data.is_following ? 'Following!' : 'Unfollowed');
    } catch (error) {
      console.error('Failed to follow:', error);
      toast.error('Failed to update follow status');
    } finally {
      setFollowLoadingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  const renderUserCard = (user: ConnectionUser): ReactElement => {
    const isFollowLoading = followLoadingIds.has(user.user_id);

    return (
      <div
        key={user.user_id}
        className="rounded-xl p-4 flex items-center gap-4 transition-all hover:shadow-md hover:scale-[1.01] dark:hover:shadow-lg"
        style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}
      >
        {/* Avatar */}
        <div
          className="w-12 h-12 rounded-full flex-shrink-0 overflow-hidden cursor-pointer hover:scale-105 transition-transform"
          onClick={() => navigate(`/user/${user.user_id}`)}
        >
          {user.picture ? (
            <img
              src={getImageUrl(user.picture) || undefined}
              alt={user.name}
              className="w-full h-full object-cover"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, var(--st-border-light) 0%, var(--st-border-medium) 100%)' }}
            >
              <User className="w-6 h-6" style={{ color: 'var(--st-primary)' }} />
            </div>
          )}
        </div>

        {/* Name and Handle */}
        <div
          className="flex-grow min-w-0 cursor-pointer"
          onClick={() => navigate(`/user/${user.user_id}`)}
        >
          <h3
            className="font-bold text-sm truncate"
            style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
          >
            {user.name}
          </h3>
          {user.handle && (
            <p
              className="text-xs truncate"
              style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
            >
              @{user.handle}
            </p>
          )}
          {user.story_count && user.story_count > 0 && (
            <p
              className="text-xs mt-0.5 flex items-center gap-1"
              style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
            >
              <BookOpen className="w-3 h-3" />
              {user.story_count} {user.story_count === 1 ? 'story' : 'stories'}
            </p>
          )}
        </div>

        {/* Follow/Unfollow Button */}
        <button
          onClick={() => handleFollow(user.user_id)}
          disabled={isFollowLoading}
          className="flex-shrink-0 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl font-semibold text-xs transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
          style={{
            background: user.is_following
              ? 'var(--st-bg-surface)'
              : 'linear-gradient(135deg, var(--st-primary) 0%, var(--st-primary-hover) 100%)',
            color: user.is_following ? 'var(--st-text-muted)' : 'var(--st-bg-page)',
            border: user.is_following ? '1px solid var(--st-border-light)' : 'none',
            fontFamily: 'Quicksand, sans-serif'
          }}
        >
          {isFollowLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : user.is_following ? (
            <>
              <UserCheck className="w-3.5 h-3.5" />
              Following
            </>
          ) : (
            <>
              <UserPlus className="w-3.5 h-3.5" />
              Follow
            </>
          )}
        </button>
      </div>
    );
  };

  const renderEmptyState = (): ReactElement => {
    const isFollowing = activeTab === 'following';

    return (
      <div className="text-center py-12">
        <div
          className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center"
          style={{ background: 'linear-gradient(135deg, var(--st-border-light) 0%, var(--st-border-medium) 100%)' }}
        >
          {isFollowing ? (
            <Heart className="w-10 h-10" style={{ color: 'var(--st-primary)' }} />
          ) : (
            <Users className="w-10 h-10" style={{ color: 'var(--st-primary)' }} />
          )}
        </div>
        <h3
          className="text-lg font-bold mb-2"
          style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
        >
          {isFollowing ? "Not following anyone yet" : "No followers yet"}
        </h3>
        <p
          className="text-sm mb-6 max-w-xs mx-auto"
          style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
        >
          {isFollowing
            ? "Explore stories and follow your favorite creators to see their latest work!"
            : "Share your stories with the community to grow your audience!"}
        </p>
        <button
          onClick={() => navigate(isFollowing ? '/explore' : '/create')}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all hover:scale-[1.02] active:scale-[0.98]"
          style={{
            background: 'linear-gradient(135deg, var(--st-primary) 0%, var(--st-primary-hover) 100%)',
            color: 'white',
            fontFamily: 'Quicksand, sans-serif'
          }}
        >
          {isFollowing ? (
            <>
              <Sparkles className="w-4 h-4" />
              Explore Stories
            </>
          ) : (
            <>
              <BookOpen className="w-4 h-4" />
              Create a Story
            </>
          )}
        </button>
      </div>
    );
  };

  return (
    <PageLayout>
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-4 py-4 md:py-6">
          {/* Page Header Card */}
          <div
            className="rounded-2xl p-4 md:p-5 mb-5 shadow-sm"
            style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}
          >
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/profile')}
                className="p-2 -ml-2 rounded-xl hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" style={{ color: 'var(--st-text-heading)' }} />
              </button>
              <div>
                <h1
                  className="text-xl md:text-2xl font-bold"
                  style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
                >
                  Connections
                </h1>
                <p
                  className="text-sm"
                  style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
                >
                  People you follow and your followers
                </p>
              </div>
            </div>
          </div>

          {/* Tabs Card */}
          <div
            className="rounded-2xl p-4 md:p-5 shadow-sm"
            style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}
          >
            {/* Tab Buttons */}
            <div
              className="flex items-center gap-1 p-1 mb-5 rounded-xl"
              style={{ background: 'var(--st-bg-muted)' }}
            >
              <button
                onClick={() => setActiveTab('following')}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm transition-all"
                style={{
                  background: activeTab === 'following' ? 'var(--st-bg-surface)' : 'transparent',
                  boxShadow: activeTab === 'following' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                  fontFamily: 'Quicksand, sans-serif',
                  color: activeTab === 'following' ? 'var(--st-text-heading)' : 'var(--st-text-muted)'
                }}
              >
                <UserCheck className="w-4 h-4" />
                Following
              </button>
              <button
                onClick={() => setActiveTab('followers')}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm transition-all"
                style={{
                  background: activeTab === 'followers' ? 'var(--st-bg-surface)' : 'transparent',
                  boxShadow: activeTab === 'followers' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                  fontFamily: 'Quicksand, sans-serif',
                  color: activeTab === 'followers' ? 'var(--st-text-heading)' : 'var(--st-text-muted)'
                }}
              >
                <Users className="w-4 h-4" />
                Followers
              </button>
            </div>

            {/* Count Badge */}
            {!loading && total > 0 && (
              <div className="flex items-center justify-between mb-4">
                <span
                  className="text-sm font-medium"
                  style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
                >
                  {total} {activeTab === 'following' ? 'following' : (total === 1 ? 'follower' : 'followers')}
                </span>
              </div>
            )}

            {/* Content */}
            {loading ? (
              <div className="text-center py-12">
                <Sparkles className="w-10 h-10 mx-auto mb-3 animate-spin" style={{ color: 'var(--st-primary)' }} />
                <p style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>Loading...</p>
              </div>
            ) : users.length === 0 ? (
              renderEmptyState()
            ) : (
              <div className="space-y-3">
                {users.map(renderUserCard)}

                {/* Load more trigger */}
                {hasMore && (
                  <div ref={loaderRef} className="text-center py-4">
                    {loadingMore && (
                      <Loader2 className="w-6 h-6 mx-auto animate-spin" style={{ color: 'var(--st-primary)' }} />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default ConnectionsPage;
